import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';
import { EquipmentDetail } from './EquipmentDetail.jsx';
import { TemperatureReportPanel } from './TemperatureReportPanel.jsx';
import { HubAdminModal } from './HubAdminModal.jsx';
import { QuickLogTempBtn } from './QuickLogTempBtn.jsx';

export function EquipmentView({ location, user }) {
  const EQ_TYPES = ['Refrigerator','Freezer','Walk-in Cooler','Espresso Machine','Grinder','Oven','Dishwasher','HVAC','Ice Maker','Other'];
  const TYPE_ICONS = { Refrigerator:'🧊', Freezer:'❄️', 'Walk-in Cooler':'🚪', 'Espresso Machine':'☕', Grinder:'⚙️', Oven:'🔥', Dishwasher:'🫧', HVAC:'🌡️', 'Ice Maker':'🧊', Other:'🔧' };
  // Component is now also rendered inside Knowledge Hub for non-manager
  // staff (read-only mode). Edit/Add/Delete affordances gate on isManager;
  // staff still see equipment + the linked SOP/manual PDFs.
  const isManager = window.EmpireRoles.isManager(user);
  const [equipment, setEquipment] = React.useState([]);
  const [vendors, setVendors] = React.useState([]);
  const [view, setView] = React.useState('list');
  const [selectedEq, setSelectedEq] = React.useState(null);
  const [showAddEq, setShowAddEq] = React.useState(false);
  const [showAddVendor, setShowAddVendor] = React.useState(false);
  const [showArchived, setShowArchived] = React.useState(false);
  const [showHubAdmin, setShowHubAdmin] = React.useState(false);
  // Global sensor-alert kill-switch (app_settings.temperature_alerts_enabled).
  // Owner-only toggle. Read once on mount + after each toggle so the
  // UI matches DB truth even if another tab flipped it.
  const [sensorAlertsOn, setSensorAlertsOn] = React.useState(false);
  const [sensorAlertsBusy, setSensorAlertsBusy] = React.useState(false);
  const isOwnerLevel = window.EmpireRoles?.isOwner?.(user) || false;
  React.useEffect(() => {
    if (!isOwnerLevel) return;
    api('GET', '/api/admin/settings', null, { 'x-user-id': String(user.id) })
      .then(r => setSensorAlertsOn((r?.settings?.temperature_alerts_enabled || 'false') === 'true'))
      .catch(() => {});
  }, [isOwnerLevel, user.id]);
  async function toggleSensorAlerts(next) {
    setSensorAlertsBusy(true);
    try {
      await api('PUT', '/api/admin/settings/temperature_alerts_enabled',
        { value: next ? 'true' : 'false' },
        { 'x-user-id': String(user.id) });
      setSensorAlertsOn(next);
    } catch (_) {} finally { setSensorAlertsBusy(false); }
  }
  const [eqForm, setEqForm] = React.useState({ name:'', type:'Refrigerator', serial_number:'', model:'', install_date:'', notes:'', requires_temp_log: false, measurement_unit:'°F', vendor_id:'' });
  const [editEq, setEditEq] = React.useState(null);
  const [editEqForm, setEditEqForm] = React.useState({});
  const [vendorForm, setVendorForm] = React.useState({ name:'', phone:'', email:'', specialty:'', notes:'' });
  const [saving, setSaving] = React.useState(false);

  async function loadEquipment(archived) {
    const q = (archived ?? showArchived) ? '&include_archived=true' : '';
    setEquipment(await api('GET', `/api/equipment?location_id=${location.id}${q}`));
  }
  async function loadVendors() { setVendors(await api('GET', '/api/vendors')); }
  React.useEffect(() => { loadEquipment(); loadVendors(); }, [location.id]);

  async function handleToggleArchived(val) {
    setShowArchived(val);
    loadEquipment(val);
  }

  async function handleAddEq(e) {
    e.preventDefault(); setSaving(true);
    try {
      const result = await api('POST', '/api/equipment', {
        ...eqForm,
        location_id: location.id,
        requires_temp_log: eqForm.requires_temp_log ? 1 : 0,
        measurement_unit: eqForm.requires_temp_log ? eqForm.measurement_unit : '',
      });
      if (eqForm.vendor_id && result?.id) {
        await api('POST', `/api/equipment/${result.id}/vendors`, { vendor_id: parseInt(eqForm.vendor_id) });
      }
      setShowAddEq(false);
      setEqForm({ name:'', type:'Refrigerator', serial_number:'', model:'', install_date:'', notes:'', requires_temp_log: false, measurement_unit:'°F', vendor_id:'' });
      loadEquipment();
    } finally { setSaving(false); }
  }
  async function handleArchiveEq(id) {
    if (!confirm('Archive this equipment? It will be hidden from the list but can be restored.')) return;
    await api('PUT', `/api/equipment/${id}/archive`); loadEquipment();
  }
  async function handleRestoreEq(id) {
    await api('PUT', `/api/equipment/${id}/restore`); loadEquipment();
  }
  async function handleDeleteEq(id, name) {
    if (!confirm(`Permanently delete "${name}" and ALL its temperature logs, parts, and service history? This cannot be undone.`)) return;
    await api('DELETE', `/api/equipment/${id}`); loadEquipment();
  }
  async function handleAddVendor(e) {
    e.preventDefault(); setSaving(true);
    try {
      await api('POST', '/api/vendors', vendorForm);
      setShowAddVendor(false); setVendorForm({ name:'', phone:'', email:'', specialty:'', notes:'' }); loadVendors();
    } finally { setSaving(false); }
  }
  async function handleDeleteVendor(id) {
    if (!confirm('Delete this vendor?')) return;
    await api('DELETE', `/api/vendors/${id}`); loadVendors();
  }

  function openEditEq(eq) {
    setEditEq(eq);
    setEditEqForm({
      name: eq.name, type: eq.type, serial_number: eq.serial_number || '',
      model: eq.model || '', install_date: eq.install_date || '', notes: eq.notes || '',
      requires_temp_log: !!eq.requires_temp_log, measurement_unit: eq.measurement_unit || '°F',
      vendor_id: eq.vendors?.[0]?.id?.toString() || '',
    });
  }

  async function handleEditEqSave(e) {
    e.preventDefault(); setSaving(true);
    try {
      await api('PUT', `/api/equipment/${editEq.id}`, {
        ...editEqForm,
        requires_temp_log: editEqForm.requires_temp_log ? 1 : 0,
        measurement_unit: editEqForm.requires_temp_log ? editEqForm.measurement_unit : '',
      });
      const oldVendorId = editEq.vendors?.[0]?.id;
      const newVendorId = editEqForm.vendor_id ? parseInt(editEqForm.vendor_id) : null;
      if (oldVendorId !== newVendorId) {
        if (oldVendorId) await api('DELETE', `/api/equipment/${editEq.id}/vendors/${oldVendorId}`);
        if (newVendorId) await api('POST', `/api/equipment/${editEq.id}/vendors`, { vendor_id: newVendorId });
      }
      setEditEq(null); loadEquipment();
    } finally { setSaving(false); }
  }

  if (view === 'detail' && selectedEq) {
    return <EquipmentDetail equipment={selectedEq} vendors={vendors} locationId={location.id} userId={user.id} isManager={isManager}
      onBack={() => { setView('list'); setSelectedEq(null); loadEquipment(); }}
      onArchived={() => { setView('list'); setSelectedEq(null); loadEquipment(); }}
      onDeleted={() => { setView('list'); setSelectedEq(null); loadEquipment(); }}/>;
  }

  const activeEquipment = equipment.filter(eq => !eq.status || eq.status === 'active');
  const archivedEquipment = equipment.filter(eq => eq.status === 'archived');

  return (
    <div className="fade-in">
      <div className="flex gap-2 mb-6 border-b border-gray-200">
        <button onClick={() => setView('list')}
          className={`px-4 py-2 text-sm font-semibold border-b-2 transition-all ${view!=='vendors'?'border-crimson text-crimson':'border-transparent text-gray-500 hover:text-gray-700'}`}>
          Equipment ({activeEquipment.length})
        </button>
        <button onClick={() => setView('vendors')}
          className={`px-4 py-2 text-sm font-semibold border-b-2 transition-all ${view==='vendors'?'border-crimson text-crimson':'border-transparent text-gray-500 hover:text-gray-700'}`}>
          Vendors ({vendors.length})
        </button>
      </div>

      {view !== 'vendors' && isManager && (
        <TemperatureReportPanel
          equipment={activeEquipment.filter(eq => eq.requires_temp_log)}
          locationId={location.id} locationName={location.name}
          isOwner={isOwnerLevel} user={user} />
      )}
      {view !== 'vendors' && (
        <div>
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            {isManager ? (
              <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                <input type="checkbox" checked={showArchived} onChange={e => handleToggleArchived(e.target.checked)} className="rounded" />
                Show archived ({archivedEquipment.length})
              </label>
            ) : <span />}
            {isOwnerLevel && (
              <label className="flex items-center gap-2 text-sm text-gray-500 select-none px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed"
                title="CJ 2026-05-22: Sensor alerts are locked OFF in server.js (SENSOR_ALERTS_GLOBALLY_FORCED_OFF = true). Notifications you may still be seeing on your phone are from the MOCREO Life app, not Monarch. Disable them there: open MOCREO app → settings → notifications.">
                <input type="checkbox" disabled checked={false} className="rounded" />
                <span>Sensor alerts: <strong className="text-gray-500">LOCKED OFF</strong>
                  <span className="text-xs text-gray-500 block mt-0.5">Code-locked. Edit server.js to re-enable.</span>
                </span>
              </label>
            )}
            {isOwnerLevel && (
              <button onClick={() => setShowHubAdmin(true)}
                className="px-3 py-2 bg-blue-600 text-white text-sm rounded-lg font-medium hover:bg-blue-700 active:scale-95 transition-all">🛰 Hubs</button>
            )}
            {isManager && (
              <button onClick={() => setShowAddEq(s=>!s)}
                className="px-3 py-2 bg-crimson text-white text-sm rounded-lg font-medium hover:bg-crimson-dark active:scale-95 transition-all">+ Add Equipment</button>
            )}
          </div>
          {showHubAdmin && (
            <HubAdminModal currentUser={user} onClose={() => setShowHubAdmin(false)} />
          )}
          {isManager && showAddEq && (
            <form onSubmit={handleAddEq} className="bg-white rounded-xl border border-gray-200 p-5 mb-4 space-y-3 fade-in shadow-sm">
              <h3 className="font-semibold text-sm text-ink">New Equipment</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input value={eqForm.name} onChange={e=>setEqForm(f=>({...f,name:e.target.value}))} required
                  className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-ink bg-white focus:outline-none" placeholder="Name *"/>
                <select value={eqForm.type} onChange={e=>setEqForm(f=>({...f,type:e.target.value}))}
                  className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-ink bg-white focus:outline-none">
                  {EQ_TYPES.map(t=><option key={t}>{t}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input value={eqForm.serial_number} onChange={e=>setEqForm(f=>({...f,serial_number:e.target.value}))}
                  className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-ink bg-white focus:outline-none" placeholder="Serial number"/>
                <input value={eqForm.model} onChange={e=>setEqForm(f=>({...f,model:e.target.value}))}
                  className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-ink bg-white focus:outline-none" placeholder="Model"/>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Install date</label>
                  <input type="date" value={eqForm.install_date} onChange={e=>setEqForm(f=>({...f,install_date:e.target.value}))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-ink bg-white focus:outline-none"/>
                </div>
                <input value={eqForm.notes} onChange={e=>setEqForm(f=>({...f,notes:e.target.value}))}
                  className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-ink bg-white focus:outline-none" placeholder="Notes"/>
              </div>
              <select value={eqForm.vendor_id} onChange={e=>setEqForm(f=>({...f,vendor_id:e.target.value}))}
                className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-ink bg-white focus:outline-none">
                <option value="">No vendor</option>
                {vendors.map(v=><option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={eqForm.requires_temp_log} onChange={e=>setEqForm(f=>({...f,requires_temp_log:e.target.checked}))} className="rounded"/>
                  <span className="text-sm text-gray-700">Requires temperature logging</span>
                </label>
                {eqForm.requires_temp_log && (
                  <div className="flex items-center gap-2 pl-6">
                    <label className="text-xs text-gray-500 flex-shrink-0">Measurement unit:</label>
                    <input value={eqForm.measurement_unit} onChange={e=>setEqForm(f=>({...f,measurement_unit:e.target.value}))}
                      className="w-24 border border-gray-200 rounded-lg px-3 py-1.5 text-sm text-ink bg-white focus:outline-none" placeholder="e.g. °F"/>
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={() => setShowAddEq(false)} className="flex-1 py-2 rounded-lg border border-gray-200 text-sm text-gray-600">Cancel</button>
                <button type="submit" disabled={saving} className="flex-1 py-2 rounded-lg bg-crimson text-white text-sm font-semibold hover:bg-crimson-dark disabled:opacity-50">{saving?'Saving...':'Add Equipment'}</button>
              </div>
            </form>
          )}
          {isManager && editEq && (
            <form onSubmit={handleEditEqSave} className="bg-white rounded-xl border border-crimson/30 p-5 mb-4 space-y-3 fade-in shadow-sm">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-sm text-ink">Edit: {editEq.name}</h3>
                <button type="button" onClick={() => setEditEq(null)} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input value={editEqForm.name} onChange={e=>setEditEqForm(f=>({...f,name:e.target.value}))} required
                  className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-ink bg-white focus:outline-none" placeholder="Name *"/>
                <select value={editEqForm.type} onChange={e=>setEditEqForm(f=>({...f,type:e.target.value}))}
                  className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-ink bg-white focus:outline-none">
                  {EQ_TYPES.map(t=><option key={t}>{t}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input value={editEqForm.serial_number} onChange={e=>setEditEqForm(f=>({...f,serial_number:e.target.value}))}
                  className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-ink bg-white focus:outline-none" placeholder="Serial number"/>
                <input value={editEqForm.model} onChange={e=>setEditEqForm(f=>({...f,model:e.target.value}))}
                  className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-ink bg-white focus:outline-none" placeholder="Model"/>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Install date</label>
                  <input type="date" value={editEqForm.install_date} onChange={e=>setEditEqForm(f=>({...f,install_date:e.target.value}))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-ink bg-white focus:outline-none"/>
                </div>
                <input value={editEqForm.notes} onChange={e=>setEditEqForm(f=>({...f,notes:e.target.value}))}
                  className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-ink bg-white focus:outline-none" placeholder="Notes"/>
              </div>
              <select value={editEqForm.vendor_id||''} onChange={e=>setEditEqForm(f=>({...f,vendor_id:e.target.value}))}
                className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-ink bg-white focus:outline-none">
                <option value="">No vendor</option>
                {vendors.map(v=><option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={editEqForm.requires_temp_log} onChange={e=>setEditEqForm(f=>({...f,requires_temp_log:e.target.checked}))} className="rounded"/>
                <span className="text-sm text-gray-700">Requires temperature logging</span>
              </label>
              {editEqForm.requires_temp_log && (
                <div className="flex items-center gap-2 pl-6">
                  <label className="text-xs text-gray-500 flex-shrink-0">Measurement unit:</label>
                  <input value={editEqForm.measurement_unit} onChange={e=>setEditEqForm(f=>({...f,measurement_unit:e.target.value}))}
                    className="w-24 border border-gray-200 rounded-lg px-3 py-1.5 text-sm text-ink bg-white focus:outline-none" placeholder="e.g. °F"/>
                </div>
              )}
              <div className="flex gap-2">
                <button type="button" onClick={() => setEditEq(null)} className="flex-1 py-2 rounded-lg border border-gray-200 text-sm text-gray-600">Cancel</button>
                <button type="submit" disabled={saving} className="flex-1 py-2 rounded-lg bg-crimson text-white text-sm font-semibold hover:bg-crimson-dark disabled:opacity-50">{saving?'Saving...':'Save Changes'}</button>
              </div>
            </form>
          )}

          {activeEquipment.length === 0 && !showArchived ? (
            <div className="text-center py-12 text-gray-400"><p className="text-3xl mb-2">🔧</p><p className="text-sm">No equipment registered</p></div>
          ) : (
            <div className="space-y-3">
              {activeEquipment.map(eq => (
                <div key={eq.id} className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm hover:shadow-md transition-shadow task-card">
                  <div className="flex items-start gap-3 flex-wrap sm:flex-nowrap">
                    <div className="text-2xl flex-shrink-0">{TYPE_ICONS[eq.type]||'🔧'}</div>
                    <div className="flex-1 min-w-0 cursor-pointer" style={{ flexBasis: '60%' }} onClick={() => { setSelectedEq(eq); setView('detail'); }}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-sm text-ink">{eq.name}</p>
                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{eq.type}</span>
                        {eq.last_temp != null && (
                          <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${eq.last_temp>41?'bg-red-100 text-red-700':eq.last_temp>38?'bg-yellow-100 text-yellow-700':'bg-green-100 text-green-700'}`}>
                            {eq.last_temp}{eq.measurement_unit||'°F'}
                          </span>
                        )}
                      </div>
                      {eq.serial_number && <p className="text-xs text-gray-400 mt-0.5">S/N: {eq.serial_number}</p>}
                      {eq.last_temp_at && <p className="text-xs text-gray-400">Last: {new Date(eq.last_temp_at).toLocaleString('en-US',{month:'short',day:'numeric',hour:'numeric',minute:'2-digit'})}</p>}
                    </div>
                    {/* Actions — wrap to a second line on narrow viewports
                        so they never crash into the temp badge above. */}
                    <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto sm:flex-shrink-0 sm:flex-nowrap mt-2 sm:mt-0">
                      {eq.requires_temp_log ? <QuickLogTempBtn equipment={eq} locationId={location.id} userId={user.id} onLogged={loadEquipment}/> : null}
                      <button onClick={() => { setSelectedEq(eq); setView('detail'); }}
                        className="px-2 py-1.5 bg-gray-100 text-gray-700 text-xs rounded-lg hover:bg-gray-200 transition-all">Details →</button>
                      {isManager && (
                        <>
                          <button onClick={() => openEditEq(eq)}
                            className="px-2 py-1.5 bg-blue-50 text-blue-700 text-xs rounded-lg hover:bg-blue-100 transition-all">Edit</button>
                          <button onClick={() => handleArchiveEq(eq.id)}
                            className="px-2 py-1.5 bg-amber-50 text-amber-700 text-xs rounded-lg hover:bg-amber-100 transition-all">Archive</button>
                          <button onClick={() => handleDeleteEq(eq.id, eq.name)}
                            className="px-2 py-1.5 bg-red-50 text-red-600 text-xs rounded-lg hover:bg-red-100 transition-all">Delete</button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {showArchived && archivedEquipment.length > 0 && (
                <>
                  <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold mt-4 mb-2">Archived</p>
                  {archivedEquipment.map(eq => (
                    <div key={eq.id} className="bg-gray-50 rounded-xl border border-gray-200 p-4 opacity-60">
                      <div className="flex items-center gap-3">
                        <div className="text-2xl flex-shrink-0 grayscale">{TYPE_ICONS[eq.type]||'🔧'}</div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-semibold text-sm text-gray-500 line-through">{eq.name}</p>
                            <span className="text-xs bg-gray-200 text-gray-500 px-2 py-0.5 rounded-full">{eq.type}</span>
                            <span className="text-xs bg-gray-200 text-gray-500 px-2 py-0.5 rounded-full">Archived</span>
                          </div>
                          {eq.serial_number && <p className="text-xs text-gray-400 mt-0.5">S/N: {eq.serial_number}</p>}
                        </div>
                        {isManager && (
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <button onClick={() => handleRestoreEq(eq.id)}
                              className="px-2 py-1.5 bg-green-50 text-green-700 text-xs rounded-lg hover:bg-green-100 transition-all">Restore</button>
                            <button onClick={() => handleDeleteEq(eq.id, eq.name)}
                              className="px-2 py-1.5 bg-red-50 text-red-600 text-xs rounded-lg hover:bg-red-100 transition-all">Delete</button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {view === 'vendors' && (
        <div>
          {isManager && (
            <div className="flex justify-end mb-4">
              <button onClick={() => setShowAddVendor(s=>!s)}
                className="px-3 py-2 bg-crimson text-white text-sm rounded-lg font-medium hover:bg-crimson-dark active:scale-95 transition-all">+ Add Vendor</button>
            </div>
          )}
          {isManager && showAddVendor && (
            <form onSubmit={handleAddVendor} className="bg-white rounded-xl border border-gray-200 p-5 mb-4 space-y-3 fade-in shadow-sm">
              <h3 className="font-semibold text-sm text-ink">New Vendor</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input value={vendorForm.name} onChange={e=>setVendorForm(f=>({...f,name:e.target.value}))} required
                  className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-ink bg-white focus:outline-none" placeholder="Vendor name *"/>
                <input value={vendorForm.specialty} onChange={e=>setVendorForm(f=>({...f,specialty:e.target.value}))}
                  className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-ink bg-white focus:outline-none" placeholder="Specialty (e.g. Refrigeration)"/>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input value={vendorForm.phone} onChange={e=>setVendorForm(f=>({...f,phone:e.target.value}))}
                  className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-ink bg-white focus:outline-none" placeholder="Phone"/>
                <input type="email" value={vendorForm.email} onChange={e=>setVendorForm(f=>({...f,email:e.target.value}))}
                  className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-ink bg-white focus:outline-none" placeholder="Email"/>
              </div>
              <textarea value={vendorForm.notes} onChange={e=>setVendorForm(f=>({...f,notes:e.target.value}))}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-ink bg-white focus:outline-none" placeholder="Notes" rows={2}/>
              <div className="flex gap-2">
                <button type="button" onClick={() => setShowAddVendor(false)} className="flex-1 py-2 rounded-lg border border-gray-200 text-sm text-gray-600">Cancel</button>
                <button type="submit" disabled={saving} className="flex-1 py-2 rounded-lg bg-crimson text-white text-sm font-semibold hover:bg-crimson-dark disabled:opacity-50">{saving?'Saving...':'Add Vendor'}</button>
              </div>
            </form>
          )}
          {vendors.length === 0 ? (
            <div className="text-center py-12 text-gray-400"><p className="text-3xl mb-2">🏢</p><p className="text-sm">No vendors added</p></div>
          ) : (
            <div className="space-y-3">
              {vendors.map(v => (
                <div key={v.id} className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <p className="font-semibold text-sm text-ink">{v.name}</p>
                      {v.specialty && <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full border border-blue-100">{v.specialty}</span>}
                    </div>
                    <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                      {v.phone && <span>📞 {v.phone}</span>}
                      {v.email && <span>✉ {v.email}</span>}
                    </div>
                    {v.notes && <p className="text-xs text-gray-400 mt-1">{v.notes}</p>}
                  </div>
                  {isManager && (
                    <button onClick={() => handleDeleteVendor(v.id)} className="text-red-400 hover:text-red-600 text-xs transition-colors flex-shrink-0">Remove</button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
